import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import DungeonTile, { getTileLightBucket } from './DungeonTile';
import { isTileCorrupted } from '../map/corruptionSystem';
import chainLinkImg from '../../assets/chain_link.png';
import {
  MONSIGNOR_BOSS_ENEMY_ID,
  MONSIGNOR_GUARD_COORDS,
  MONSIGNOR_BOSS_POS,
  PRIME_VESSEL_BOSS_ENEMY_ID,
  PRIME_VESSEL_BOSS_POS,
} from '../boss/specialBossFloorConfig';


function getQuadraticBezierPoint(p0, p1, p2, t) {
  const mt = 1 - t;
  return {
    x: (mt * mt * p0.x) + (2 * mt * t * p1.x) + (t * t * p2.x),
    y: (mt * mt * p0.y) + (2 * mt * t * p1.y) + (t * t * p2.y),
  };
}

function buildQuadraticBezierArcTable(p0, p1, p2, steps = 72) {
  const samples = [{
    t: 0,
    point: { ...p0 },
    distance: 0,
  }];

  let totalDistance = 0;
  let prev = p0;

  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const point = getQuadraticBezierPoint(p0, p1, p2, t);
    totalDistance += Math.hypot(point.x - prev.x, point.y - prev.y);
    samples.push({ t, point, distance: totalDistance });
    prev = point;
  }

  return {
    totalDistance,
    samples,
  };
}

function sampleQuadraticBezierAtDistance(arcTable, targetDistance) {
  const { samples, totalDistance } = arcTable;
  if (!samples.length) return { x: 0, y: 0 };
  if (targetDistance <= 0) return { ...samples[0].point };
  if (targetDistance >= totalDistance) return { ...samples[samples.length - 1].point };

  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const next = samples[i];

    if (targetDistance > next.distance) continue;

    const span = next.distance - prev.distance;
    const ratio = span > 0 ? ((targetDistance - prev.distance) / span) : 0;
    return {
      x: prev.point.x + ((next.point.x - prev.point.x) * ratio),
      y: prev.point.y + ((next.point.y - prev.point.y) * ratio),
    };
  }

  return { ...samples[samples.length - 1].point };
}

function getRectEdgePoint(center, toward, halfWidth, halfHeight, inset = 0) {
  const dx = toward.x - center.x;
  const dy = toward.y - center.y;

  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
    return { ...center };
  }

  const safeHalfWidth = Math.max(1, halfWidth - inset);
  const safeHalfHeight = Math.max(1, halfHeight - inset);
  const tx = Math.abs(dx) > 0.001 ? safeHalfWidth / Math.abs(dx) : Number.POSITIVE_INFINITY;
  const ty = Math.abs(dy) > 0.001 ? safeHalfHeight / Math.abs(dy) : Number.POSITIVE_INFINITY;
  const scale = Math.min(tx, ty);

  return {
    x: center.x + (dx * scale),
    y: center.y + (dy * scale),
  };
}

function getMonsignorChainControlPoint(start, end, boardSpanX, boardSpanY) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.hypot(dx, dy);
  const midpointX = (start.x + end.x) / 2;
  const lowestEndY = Math.max(start.y, end.y);
  const horizontalSign = Math.sign(dx) || 1;
  const horizontalBias = Math.min(boardSpanX * 0.24, Math.abs(dx) * 0.08) * horizontalSign;
  const downwardArc = Math.max(boardSpanY * 0.25, distance * 0.16);

  return {
    x: midpointX + horizontalBias,
    y: lowestEndY + downwardArc,
  };
}

const MonsignorChainsOverlay = React.memo(function MonsignorChainsOverlay({
  map,
  mapWidth,
  mapHeight,
  boardSpanX,
  boardSpanY,
  visibleTiles,
  exploredTiles,
  renderTileWidth,
  renderTileHeight,
}) {
  const bossTile = map?.[MONSIGNOR_BOSS_POS.y]?.[MONSIGNOR_BOSS_POS.x];
  const bossKey = `${MONSIGNOR_BOSS_POS.x},${MONSIGNOR_BOSS_POS.y}`;
  const bossRevealed =
    visibleTiles?.has?.(bossKey) || exploredTiles?.has?.(bossKey);

  const chainSegments = React.useMemo(() => {
    if (!Array.isArray(map) || !bossRevealed || bossTile !== 'enemy-boss') return [];

    const bossCenter = {
      x: (MONSIGNOR_BOSS_POS.x + 0.5) * boardSpanX,
      y: (MONSIGNOR_BOSS_POS.y + 0.5) * boardSpanY,
    };

    const baseLinkWidth = Math.max(8, renderTileWidth * 0.32);
    const segmentSpacing = Math.max(9, renderTileWidth * 0.24);
    const bossHalfWidth = Math.max(12, renderTileWidth * 0.46);
    const bossHalfHeight = Math.max(12, renderTileHeight * 0.46);
    const guardHalfWidth = Math.max(12, renderTileWidth * 0.46);
    const guardHalfHeight = Math.max(12, renderTileHeight * 0.46);
    const edgeInset = 0;
    const segments = [];

    MONSIGNOR_GUARD_COORDS.forEach((guard) => {
      const guardKey = `${guard.x},${guard.y}`;
      const guardTile = map?.[guard.y]?.[guard.x];
      const guardRevealed =
        visibleTiles?.has?.(guardKey) || exploredTiles?.has?.(guardKey);

      if (!guardRevealed || guardTile !== 'enemy') return;

      const guardCenter = {
        x: (guard.x + 0.5) * boardSpanX,
        y: (guard.y + 0.5) * boardSpanY,
      };

      const start = getRectEdgePoint(
        bossCenter,
        guardCenter,
        bossHalfWidth,
        bossHalfHeight,
        edgeInset,
      );
      const end = getRectEdgePoint(
        guardCenter,
        bossCenter,
        guardHalfWidth,
        guardHalfHeight,
        edgeInset,
      );
      const control = getMonsignorChainControlPoint(start, end, boardSpanX, boardSpanY);
      const arcTable = buildQuadraticBezierArcTable(start, control, end);
      const curveLength = arcTable.totalDistance;
      const segmentCount = Math.max(8, Math.floor(curveLength / segmentSpacing) + 1);
      const startDistance = 0;

      for (let i = 0; i < segmentCount; i++) {
        const distance = Math.min(curveLength, startDistance + (i * segmentSpacing));
        const point = sampleQuadraticBezierAtDistance(arcTable, distance);
        const prev = sampleQuadraticBezierAtDistance(arcTable, Math.max(0, distance - 2));
        const next = sampleQuadraticBezierAtDistance(arcTable, Math.min(curveLength, distance + 2));
        const angle = Math.atan2(next.y - prev.y, next.x - prev.x) * (180 / Math.PI);

        segments.push({
          id: `${guardKey}-${i}`,
          left: point.x,
          top: point.y,
          angle,
          width: baseLinkWidth,
          opacity: 0.94,
        });
      }
    });

    return segments;
  }, [
    map,
    bossRevealed,
    bossTile,
    boardSpanX,
    boardSpanY,
    visibleTiles,
    exploredTiles,
    renderTileWidth,
    renderTileHeight,
  ]);

  if (!chainSegments.length) return null;

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        width: mapWidth * boardSpanX,
        height: mapHeight * boardSpanY,
        zIndex: 120,
        overflow: 'visible',
      }}
      aria-hidden="true"
    >
      {chainSegments.map((segment) => (
        <img
          key={segment.id}
          src={chainLinkImg}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: segment.left,
            top: segment.top,
            width: segment.width,
            height: 'auto',
            transform: `translate(-50%, -50%) rotate(${segment.angle}deg)`,
            transformOrigin: 'center center',
            pointerEvents: 'none',
            userSelect: 'none',
            opacity: segment.opacity,
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.55))',
          }}
        />
      ))}
    </div>
  );
});

function DungeonBoard({
  boardRefs,
  boardState,
  renderState,
  tileActions,
  tileHelpers,
  tileAssets,
}) {
  const { boardViewportRef, boardElRef } = boardRefs;

  const {
    map,
    mapWidth,
    mapHeight,
    playerPos,
    tileViewport,
    ambientPropRenders = [],
    underPropRenders = [],
    spiderwebKeys,
    spiderwebShakeTicksByKey,
  } = boardState;

  const primeVesselDestroyedDeviceOverlays = React.useMemo(() => {
    const isPrimeVesselBossAlive =
      renderState.specialBossEnemyId === PRIME_VESSEL_BOSS_ENEMY_ID &&
      map?.[PRIME_VESSEL_BOSS_POS.y]?.[PRIME_VESSEL_BOSS_POS.x] === 'enemy-boss';

    if (!isPrimeVesselBossAlive) return [];

    const isAliveAt = (x, y) => map?.[y]?.[x] === 'enemy';
    const overlays = [];

    if (!isAliveAt(3, 3) && tileAssets?.primeVesselOverlayPrimeCrucibleImg) {
      overlays.push(tileAssets.primeVesselOverlayPrimeCrucibleImg);
    }
    if (!isAliveAt(3, 27) && tileAssets?.primeVesselOverlaySynapticRetortImg) {
      overlays.push(tileAssets.primeVesselOverlaySynapticRetortImg);
    }
    if (!isAliveAt(27, 3) && tileAssets?.primeVesselOverlayPulseEngineImg) {
      overlays.push(tileAssets.primeVesselOverlayPulseEngineImg);
    }
    if (!isAliveAt(27, 27) && tileAssets?.primeVesselOverlayAstralReliquaryImg) {
      overlays.push(tileAssets.primeVesselOverlayAstralReliquaryImg);
    }

    return overlays;
  }, [map, renderState.specialBossEnemyId, tileAssets]);

  const {
    boardPerspectiveStageStyle,
    boardPerspectiveViewportStyle,
    boardPerspectiveBoardStyle,
    boardCameraTransition,
    boardMotionScale,
    offsetX,
    offsetY,
    renderTileWidth,
    renderTileHeight,
    renderTileHalfMargin,
    torchProgress,
    lightRadius,
    boardSpanX,
    boardSpanY,
    flash,
    aggressiveLowFx,
    secretWhisperTorchFlicker,
    sanityFx,
    madnessOverlayOpacity,
    madnessOverlayBlendMode,
    sanityScreenFlicker,
    sanityDistortionSpike,
    sanityPeripheralShadow,
    sanityCollapseBurst,
    ghostEnemyFlicker,
    spiderwebShakeDurationMs,
  } = renderState;

  const { backgroundImg, madnessOverlayImg } = tileAssets;

  const distortionJitterX = sanityDistortionSpike ? Number(sanityDistortionSpike.jitterX) : 0;
  const distortionJitterY = sanityDistortionSpike ? Number(sanityDistortionSpike.jitterY) : 0;
  const distortionScale = sanityDistortionSpike ? Number(sanityDistortionSpike.scale) : 1;
  const distortionBlurPx = sanityDistortionSpike ? Number(sanityDistortionSpike.blurPx) : 0;
  const distortionBrightness = sanityDistortionSpike ? Number(sanityDistortionSpike.brightness) : 1;
  const distortionSaturate = sanityDistortionSpike ? Number(sanityDistortionSpike.saturate) : 1;

  const collapseJitterX = sanityCollapseBurst ? Number(sanityCollapseBurst.boardJitterX) : 0;
  const collapseJitterY = sanityCollapseBurst ? Number(sanityCollapseBurst.boardJitterY) : 0;
  const collapseScale = sanityCollapseBurst ? Number(sanityCollapseBurst.boardScale) : 1;
  const collapseBlurPx = sanityCollapseBurst ? Number(sanityCollapseBurst.boardBlurPx) : 0;
  const collapseSkewDeg = sanityCollapseBurst ? Number(sanityCollapseBurst.boardSkewDeg) : 0;

  return (
    <>
      <div
        ref={boardViewportRef}
        className="flex items-center justify-center h-full"
        style={{
          ...boardPerspectiveStageStyle,
          isolation: 'isolate',
          zIndex: 0,
        }}
      >
        <div style={boardPerspectiveViewportStyle}>
          <motion.div
            style={{
  transformOrigin: 'top left',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  transformStyle: 'preserve-3d',
  filter:
    sanityDistortionSpike || sanityCollapseBurst
      ? `blur(${distortionBlurPx + collapseBlurPx}px) brightness(${distortionBrightness}) saturate(${distortionSaturate})`
      : undefined,
}}
            animate={{
  x: offsetX + distortionJitterX + collapseJitterX,
  y: offsetY + distortionJitterY + collapseJitterY,
  scale: boardMotionScale * distortionScale * collapseScale,
  skewX: collapseSkewDeg,
}}
            transition={boardCameraTransition}
          >
            <div ref={boardElRef} style={boardPerspectiveBoardStyle}>
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${backgroundImg})`,
                  backgroundSize: 'auto',
                  backgroundRepeat: 'repeat',
                  width: `${mapWidth * boardSpanX * 2}px`,
                  height: `${mapHeight * boardSpanY * 2}px`,
                  left: `-${(mapWidth * boardSpanX) / 2}px`,
                  top: `-${(mapHeight * boardSpanY) / 2}px`,
                  zIndex: -2,
                }}
              />

              {underPropRenders.map((prop) => (
                <img
                  key={prop.id}
                  src={prop.src}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: prop.left,
                    top: prop.top,
                    width: prop.w,
                    height: prop.h,
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    opacity: prop.opacity,
                    transform: `rotate(${prop.rotateDeg}deg)`,
                    transformOrigin: 'center center',
                    mixBlendMode: prop.mixBlendMode,
                    zIndex: -1,
                  }}
                />
              ))}

              {ambientPropRenders.map((prop) => (
                <img
                  key={prop.id}
                  src={prop.src}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: prop.left,
                    top: prop.top,
                    width: prop.w,
                    height: prop.h,
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: 1,
                  }}
                />
              ))}

              {renderState.specialBossEnemyId === MONSIGNOR_BOSS_ENEMY_ID && (
                <MonsignorChainsOverlay
                  map={map}
                  mapWidth={mapWidth}
                  mapHeight={mapHeight}
                  boardSpanX={boardSpanX}
                  boardSpanY={boardSpanY}
                  visibleTiles={boardState.visibleTiles}
                  exploredTiles={boardState.exploredTiles}
                  renderTileWidth={renderTileWidth}
                  renderTileHeight={renderTileHeight}
                />
              )}

              {map.map((row, y) => {
                if (
                  tileViewport.enabled &&
                  (y < tileViewport.yStart || y > tileViewport.yEnd)
                ) {
                  return (
                    <div
                      className="flex"
                      key={y}
                      style={{
                        width: tileViewport.rowW,
                        height: tileViewport.rowH,
                      }}
                    />
                  );
                }

                const rowSlice = tileViewport.enabled
                  ? row.slice(tileViewport.xStart, tileViewport.xEnd + 1)
                  : row;

                return (
                  <div
                    className="flex"
                    key={y}
                    style={
                      tileViewport.enabled
                        ? { width: tileViewport.rowW, height: tileViewport.rowH }
                        : undefined
                    }
                  >
                    {tileViewport.enabled && tileViewport.leftPad > 0 && (
                      <div
                        style={{
                          width: tileViewport.leftPad,
                          height: tileViewport.rowH,
                        }}
                      />
                    )}

                    {rowSlice.map((tile, xi) => {
                      const x = tileViewport.enabled
                        ? xi + tileViewport.xStart
                        : xi;

                      if (tileViewport.enabled) {
                        const rd = Math.hypot(x - playerPos.x, y - playerPos.y);
                        const hasYahaka = tileHelpers.isYahakaAtTile?.(x, y);
                        if (rd > lightRadius + 2 && !hasYahaka) {
                          return (
                            <div
                              key={x}
                              style={{
                                width: renderTileWidth,
                                height: renderTileHeight,
                                margin: renderTileHalfMargin,
                              }}
                            />
                          );
                        }
                      }

                      const tileCoordKey = `${x},${y}`;
                      const isPlayer = x === boardState.playerPos.x && y === boardState.playerPos.y;
                      const isVisible = boardState.visibleTiles.has(tileCoordKey);
                      const isExplored = isVisible || isPlayer || boardState.exploredTiles.has(tileCoordKey);
                      const hasSpiderweb = spiderwebKeys instanceof Set && spiderwebKeys.has(tileCoordKey);
                      const spiderwebShakeTick = Number(spiderwebShakeTicksByKey?.[tileCoordKey] || 0);
                      const brazierGuideRaw = renderState.showBrazierRadiusGuide
                        ? Number(renderState.brazierRadiusGuideByTile?.[tileCoordKey] || 0)
                        : 0;
                      const tileVariation = renderState.tileVariations?.[tileCoordKey] ?? null;
                      const isBrazierLitAtTile = tileHelpers.isBrazierLit(x, y);
                      const isSpecialCorruptedEnemyTile = tileHelpers.isSpecialCorruptedEnemyAtTile(x, y);
                      const isYahakaTile = !!tileHelpers.isYahakaAtTile?.(x, y);
                      const isCorruptedTile = isTileCorrupted(renderState.floorCorruption, x, y);
                      const ghostEnemyFlickerForTile =
                        ghostEnemyFlicker && ghostEnemyFlicker.x === x && ghostEnemyFlicker.y === y
                          ? ghostEnemyFlicker
                          : null;
                      const playerDeltaX = x - boardState.playerPos.x;
                      const playerDeltaY = y - boardState.playerPos.y;
                      const playerDistance = Math.hypot(playerDeltaX, playerDeltaY);
                      const torchFalloff = renderState.torchProgress > 0
                        ? Math.max(0, 1 - playerDistance / (renderState.lightRadius + 0.85))
                        : 0;
                      const storylineShiftX = playerDeltaX * (renderState.baseTileWidth * 8);
                      const storylineShiftY = playerDeltaY * (renderState.baseTileHeight * 8);
                      const lightBucket = getTileLightBucket({
                        revealed: isExplored,
                        isVisible,
                        tile,
                        torchProgress: renderState.torchProgress,
                        lightRadius: renderState.lightRadius,
                        playerDistance,
                        isBrazierLitAtTile,
                      });
                      const isYahakaGhostForm = isYahakaTile && playerDistance > 2.05;

                      return (
                        <DungeonTile
                          key={`${x}-${y}`}
                          x={x}
                          y={y}
                          tile={tile}
                          map={map}
                          tileCoordKey={tileCoordKey}
                          isPlayer={isPlayer}
                          isVisible={isVisible}
                          isExplored={isExplored}
                          hasSpiderweb={hasSpiderweb}
                          spiderwebShakeTick={spiderwebShakeTick}
                          isMobile={renderState.isMobile}
                          lowFx={renderState.lowFx}
                          aggressiveLowFx={renderState.aggressiveLowFx}
                          isStorylineActive={renderState.isStorylineActive}
                          floorLevel={renderState.floorLevel}
                          floorSeed={renderState.floorSeed}
                          torchProgress={renderState.torchProgress}
                          brazierGuideRaw={brazierGuideRaw}
                          tileVariation={tileVariation}
                          currentFloorTilesetProfile={renderState.currentFloorTilesetProfile}
                          renderTileWidth={renderState.renderTileWidth}
                          renderTileHeight={renderState.renderTileHeight}
                          renderTileHalfMargin={renderState.renderTileHalfMargin}
                          renderTileRadius={renderState.renderTileRadius}
                          runSeed={renderState.runSeed}
                          brazierGuideMinOpacity={renderState.brazierGuideMinOpacity}
                          brazierGuideMaxOpacity={renderState.brazierGuideMaxOpacity}
                          isBrazierLitAtTile={isBrazierLitAtTile}
                          isSpecialCorruptedEnemyTile={isSpecialCorruptedEnemyTile}
                          isYahakaTile={isYahakaTile}
                          isCorruptedTile={isCorruptedTile}
                          ghostEnemyFlickerForTile={ghostEnemyFlickerForTile}
                          spiderwebShakeDurationMs={spiderwebShakeDurationMs}
                          lightBucket={lightBucket}
                          torchFalloff={torchFalloff}
                          storylineShiftX={storylineShiftX}
                          storylineShiftY={storylineShiftY}
                          isYahakaGhostForm={isYahakaGhostForm}
                          specialBossEnemyId={renderState.specialBossEnemyId}
                          primeVesselDestroyedDeviceOverlays={
                            tile === 'enemy-boss' &&
                            renderState.specialBossEnemyId === PRIME_VESSEL_BOSS_ENEMY_ID &&
                            x === PRIME_VESSEL_BOSS_POS.x &&
                            y === PRIME_VESSEL_BOSS_POS.y
                              ? primeVesselDestroyedDeviceOverlays
                              : null
                          }
                          handleTileClick={tileActions.handleTileClick}
                          tileAssets={tileAssets}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>

      <div
        className="hidden lg:block absolute inset-0"
        style={{
          zIndex: 30,
          pointerEvents: 'none',
          background:
  'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) max(0px, calc(100% - 1800px)), rgba(0,0,0,1) max(0px, calc(100% - 520px)), rgba(0,0,0,1) 100%)',
        }}
      />

      

      <div
        className={`absolute top-0 left-0 w-full h-full ${
          torchProgress > 0 && !aggressiveLowFx ? 'torch-flicker' : ''
        }`}
        style={{
          zIndex: 30,
          pointerEvents: 'none',
          backgroundImage: (() => {
            const base = (lightRadius + 0.6) * boardSpanX * boardMotionScale;
const r0 = base * 0.22;
const r1 = base * 0.58;
const r2 = base * 1.0;
const r3 = base * 1.55;
const edge = torchProgress > 0 ? 0.72 : 0.95;

return [
  `radial-gradient(circle at 50% 50%, rgba(0,0,0,0) 0px, rgba(0,0,0,0.10) ${r0}px, rgba(0,0,0,0.28) ${r1}px, rgba(0,0,0,0.62) ${r2}px, rgba(0,0,0,${edge}) ${r3}px)`,
  `radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) 28%, rgba(0,0,0,0.72) 100%)`,
].join(',');
          })(),
        }}
      />

      <div
        className={`absolute top-0 left-0 w-full h-full ${
          torchProgress > 0 && !aggressiveLowFx ? 'torch-flicker' : ''
        }`}
        style={{
          zIndex: 30,
          pointerEvents: 'none',
          mixBlendMode: 'screen',
          filter: 'blur(2px)',
          backgroundImage: (() => {
            const base = (lightRadius + 0.6) * boardSpanX * boardMotionScale;
            const r1 = base * 0.55;
            const r2 = base * 1.2;

            const corruption = sanityFx?.torchCorruption ?? 0;

            const warmCore = torchProgress > 0 ? 0.22 * (1 - corruption * 0.75) : 0;
            const warmMid = 0.10 * (1 - corruption * 0.85);

            const violetCore = torchProgress > 0 ? 0.04 + corruption * 0.18 : 0;
            const violetMid = corruption * 0.10;

            return [
              `radial-gradient(circle at 50% 50%, rgba(255,200,130,${warmCore}) 0px, rgba(255,160,80,${warmMid}) ${r1}px, rgba(0,0,0,0) ${r2}px)`,
              `radial-gradient(circle at 50% 50%, rgba(170,110,255,${violetCore}) 0px, rgba(110,60,220,${violetMid}) ${r1 * 0.92}px, rgba(0,0,0,0) ${r2}px)`
            ].join(',');
          })(),
        }}
      />

      {madnessOverlayOpacity > 0 && madnessOverlayImg && (
  <div
    className="absolute inset-0 pointer-events-none overflow-hidden"
    style={{
      zIndex: 32,
    }}
  >
    {/* Base overlay: unchanged */}
    <img
      src={madnessOverlayImg}
      alt=""
      draggable={false}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center center',
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: madnessOverlayOpacity,
        mixBlendMode: madnessOverlayBlendMode || 'normal',
      }}
    />

    {/* Pulse layer */}
    <motion.img
      src={madnessOverlayImg}
      alt=""
      draggable={false}
      animate={{
        opacity: [
          0,
          Math.min(0.16, madnessOverlayOpacity + 0.06),
          0,
          Math.min(0.22, madnessOverlayOpacity + 0.10),
          0,
        ],
        scale: [1, 1.008, 1, 1.014, 1],
      }}
      transition={{
        duration: 4.6,
        times: [0, 0.12, 0.24, 0.62, 0.78],
        repeat: Infinity,
        ease: 'easeInOut',
      }}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center center',
        pointerEvents: 'none',
        userSelect: 'none',
        mixBlendMode: madnessOverlayBlendMode || 'normal',
        filter: 'brightness(1.15)',
      }}
    />
  </div>
)}

      <motion.div
        className="absolute top-0 left-0 w-full h-full"
        style={{
          zIndex: 30,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at 50% 45%, rgba(0,0,0,0) 22%, rgba(0,0,0,0.72) 65%, rgba(0,0,0,0.94) 100%)',
        }}
        animate={{
          opacity: Math.max(0.4, Math.min(1, 0.4 + (1 - torchProgress) * 0.85)),
        }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      />

      {!aggressiveLowFx && secretWhisperTorchFlicker && torchProgress > 0 && (
        <div
          key={`secret-whisper-gust-dark-${secretWhisperTorchFlicker.id}`}
          className="absolute inset-0 pointer-events-none"
          style={{
            zIndex: 30,
            willChange: 'transform, opacity, filter',
            transformOrigin: '50% 55%',
            background: `radial-gradient(
  ellipse at ${secretWhisperTorchFlicker.centerX}% ${secretWhisperTorchFlicker.centerY}%,
  rgba(0,0,0,0.05) 0%,
  rgba(0,0,0,0.16) 12%,
  rgba(0,0,0,0.34) 26%,
  rgba(0,0,0,0.58) 50%,
  rgba(0,0,0,0.80) 74%,
  rgba(0,0,0,0.92) 100%
)`,
            animation: `secretWhisperTorchGust ${secretWhisperTorchFlicker.durationMs}ms steps(${secretWhisperTorchFlicker.steps}, end) 1 both`,
            '--swf-shift-x': secretWhisperTorchFlicker.shiftX,
            '--swf-shift-y': secretWhisperTorchFlicker.shiftY,
            '--swf-rotate': secretWhisperTorchFlicker.rotate,
            '--swf-scale-min': secretWhisperTorchFlicker.scaleMin,
            '--swf-scale-max': secretWhisperTorchFlicker.scaleMax,
            '--swf-opacity-min': secretWhisperTorchFlicker.opacityMin,
            '--swf-opacity-max': secretWhisperTorchFlicker.opacityMax,
            '--swf-blur-min': secretWhisperTorchFlicker.blurMin,
            '--swf-blur-max': secretWhisperTorchFlicker.blurMax,
          }}
        />
      )}

      <AnimatePresence>
        {sanityScreenFlicker && (
          <motion.div
            key={`sanity-flicker-${sanityScreenFlicker.id}`}
            className="absolute inset-0"
            style={{
              zIndex: 34,
              pointerEvents: 'none',
              mixBlendMode: 'screen',
              filter: `blur(${sanityScreenFlicker.blurPx}px)`,
              background: `
                radial-gradient(
                  ellipse at 50% 48%,
                  rgba(180, 110, 255, ${sanityScreenFlicker.violetAlpha}) 0%,
                  rgba(120, 60, 220, ${sanityScreenFlicker.violetAlpha * 0.55}) 34%,
                  rgba(0,0,0,0) 72%
                ),
                radial-gradient(
                  ellipse at 50% 50%,
                  rgba(0,0,0,0) 26%,
                  rgba(0,0,0,${sanityScreenFlicker.darknessAlpha}) 64%,
                  rgba(0,0,0,${sanityScreenFlicker.darknessAlpha * 1.8}) 100%
                )
              `,
            }}
            initial={{ opacity: 0, scale: 1 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [1, Number(sanityScreenFlicker.scale), 1],
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: sanityScreenFlicker.durationMs / 1000,
              times: [0, 0.25, 1],
              ease: 'easeOut',
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {sanityPeripheralShadow && (
          <motion.div
            key={`sanity-shadow-${sanityPeripheralShadow.id}`}
            className="absolute pointer-events-none"
            style={{
              zIndex: 33,
              ...(sanityPeripheralShadow.side === 'left'
                ? {
                    left: 0,
                    top: `${50 - sanityPeripheralShadow.heightPct / 2}%`,
                    width: `${sanityPeripheralShadow.widthPct}%`,
                    height: `${sanityPeripheralShadow.heightPct}%`,
                  }
                : sanityPeripheralShadow.side === 'right'
                  ? {
                      right: 0,
                      top: `${50 - sanityPeripheralShadow.heightPct / 2}%`,
                      width: `${sanityPeripheralShadow.widthPct}%`,
                      height: `${sanityPeripheralShadow.heightPct}%`,
                    }
                  : sanityPeripheralShadow.side === 'top'
                    ? {
                        top: 0,
                        left: `${50 - sanityPeripheralShadow.widthPct / 2}%`,
                        width: `${sanityPeripheralShadow.widthPct}%`,
                        height: `${sanityPeripheralShadow.heightPct}%`,
                      }
                    : {
                        bottom: 0,
                        left: `${50 - sanityPeripheralShadow.widthPct / 2}%`,
                        width: `${sanityPeripheralShadow.widthPct}%`,
                        height: `${sanityPeripheralShadow.heightPct}%`,
                      }),
              filter: `blur(${sanityPeripheralShadow.blurPx}px)`,
              background:
                'radial-gradient(ellipse at center, rgba(20,0,30,0.0) 0%, rgba(25,0,45,0.16) 26%, rgba(0,0,0,0.34) 58%, rgba(0,0,0,0) 100%)',
            }}
            initial={{
              opacity: 0,
              x:
                sanityPeripheralShadow.side === 'left'
                  ? -Number(sanityPeripheralShadow.driftPx)
                  : sanityPeripheralShadow.side === 'right'
                    ? Number(sanityPeripheralShadow.driftPx)
                    : 0,
              y:
                sanityPeripheralShadow.side === 'top'
                  ? -Number(sanityPeripheralShadow.driftPx)
                  : sanityPeripheralShadow.side === 'bottom'
                    ? Number(sanityPeripheralShadow.driftPx)
                    : 0,
              scale: 0.96,
            }}
            animate={{
              opacity: [0, sanityPeripheralShadow.opacity, 0],
              x: 0,
              y: 0,
              scale: [0.96, 1.02, 1.04],
            }}
            exit={{ opacity: 0 }}
            transition={{
              duration: sanityPeripheralShadow.durationMs / 1000,
              times: [0, 0.22, 1],
              ease: 'easeOut',
            }}
          />
        )}
      </AnimatePresence>


      <AnimatePresence>
        {flash && (
          <motion.div
            key="torch-ignite-flash"
            className="absolute inset-0"
            style={{
              zIndex: 31,
              pointerEvents: 'none',
              mixBlendMode: 'screen',
              filter: 'blur(2px)',
              background:
                'radial-gradient(ellipse at 50% 55%, rgba(255,200,90,0.55) 0%, rgba(255,150,60,0.22) 38%, rgba(0,0,0,0) 72%)',
            }}
            initial={{ opacity: 0, scale: 1 }}
            animate={{ opacity: [0, 0.85, 0], scale: [1, 1.03, 1] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.38, times: [0, 0.22, 1], ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {flash && (
          <motion.div
            key="torch-ignite-edge-lift"
            className="absolute inset-0"
            style={{
              zIndex: 32,
              pointerEvents: 'none',
              mixBlendMode: 'screen',
              filter: 'blur(3px)',
              background:
                'radial-gradient(ellipse at 50% 55%, rgba(0,0,0,0) 45%, rgba(255,190,90,0.18) 72%, rgba(255,150,60,0.26) 92%, rgba(255,150,60,0.22) 100%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.55, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.32, times: [0, 0.25, 1], ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export default React.memo(DungeonBoard);
